// src/components/ContactSellerModal.jsx
import React, { useEffect, useState } from "react"; // ← add useState
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { contactRequestSchema } from "../validation/contactRequest.schema";
import api from "../api/axios";
import { toast } from "react-toastify";

/**
 * Even wider, no-height-limit Modal
 */
function Modal({ open, onClose, children, titleId }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[#1f2954]/90 p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="relative w-full max-w-4xl  max-h-[90vh] overflow-y-auto bg-white text-primary rounded-xl shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-8 sm:top-4 right-4 text-secondary hover:text-on-surface
                     focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <span className="sr-only">Close</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

export default function ContactSellerModal({
  open,
  onClose,
  vehicleId,
  regNumber,
  includeWarranty,
  warrantySummary,
  vehiclePriceInfo,
  make,
  model,
  mileage,
  ref,
  financeData,
}) {
  console.log("financeData", financeData);

  // ─── NEW: state to hold the server‐generated requestId ─────────────────
  const [requestId, setRequestId] = useState(null);
  const [showFinancePrompt, setShowFinancePrompt] = useState(false);
  const [showFinanceInputs, setShowFinanceInputs] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(contactRequestSchema, {
      context: { requiresPayment: true },
    }),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      paymentOption: "",
      baseDeposit: "",
      creditScore: "",
      termLength: "",
      agreementType: "",
      expectedMileage: "",
      message: "",
    },
  });

  const paymentOption = watch("paymentOption");

  // ✅ NEW: Combined close handler
  const handleModalClose = () => {
    // reset(); // reset form fields
    reset({
      fullName: "",
      email: "",
      phone: "",
      paymentOption: "",
      baseDeposit: "",
      creditScore: "",
      termLength: "",
      agreementType: "",
      expectedMileage: "",
      message: "",
    });
    setRequestId(null); // reset thank-you screen
    onClose(); // close the modal
  };

  useEffect(() => {
    if (open) {
      reset();
      setRequestId(null);
    }
  }, [open, reset]);

  useEffect(() => {
    // When paymentOption changes, clear related fields
    if (paymentOption === "installments") {
      if (financeData) {
        // Already handled in Step 1
        setShowFinancePrompt(false);
        setShowFinanceInputs(true);
      } else {
        // Show prompt only if no financeData
        setShowFinancePrompt(true);
        setShowFinanceInputs(false);
      }
    } else {
      setShowFinancePrompt(false);
      setShowFinanceInputs(false); // hide finance inputs if not installments

      reset(
        {
          ...watch(), // keep other fields (like fullName, email, phone)
          baseDeposit: "",
          creditScore: "",
          termLength: "",
          agreementType: "",
          expectedMileage: "",
        },
        { keepErrors: true, keepDirty: false }
      );
    }
  }, [paymentOption, financeData]);

  useEffect(() => {
    if (open && financeData) {
      // 1️⃣ Set paymentOption to installments
      setValue("paymentOption", "installments");

      // 2️⃣ Show finance inputs directly
      setShowFinanceInputs(true);
      setShowFinancePrompt(false);

      // 3️⃣ Pre-fill all finance-related fields
      setValue("baseDeposit", financeData.deposit || "");
      setValue("creditScore", financeData.creditScore || "");
      setValue(
        "termLength",
        financeData.termLength ? `${financeData.termLength} months` : ""
      );
      setValue(
        "agreementType",
        financeData.financeType === "HP"
          ? "Hire Purchase (HP)"
          : "Personal Contract Purchase (PCP)"
      );
      setValue("expectedMileage", financeData.annualMileage || "");
    }
  }, [open, financeData, setValue]);

  // useEffect(() => {
  //   if (paymentOption === "installments") {
  //   } else {
  //     setShowFinancePrompt(false);
  //     setShowFinanceInputs(false); // hide finance inputs if not installments
  //   }
  // }, [paymentOption]);

  const onSubmit = async (data) => {
    try {
      // Prepare full payload including vehicle summary
      const payload = {
        ...data, // form fields: fullName, email, phone, paymentOption, etc.
        vehicle: {
          id: vehicleId,
          regNumber,
          make,
          model,
          mileage,
          priceInfo: vehiclePriceInfo,
          warranty: (includeWarranty && warrantySummary) || null,
        },
      };

      // POST to backend
      const response = await api.post("/requests", payload);

      // Extract requestId
      const { requestId: newRequestId } = response.data;
      setRequestId(newRequestId);
      toast.success(`Request sent! Your reference ID is ${newRequestId}.`);
      reset();
    } catch (err) {
      console.error("Request failed", err);
      toast.error("Oops—something went wrong. Please try again.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      titleId="contact-seller-title"
    >
      {/* ── A) If requestId is set, show a thank‐you screen; else show the form ── */}
      {requestId ? (
        <div className="text-center space-y-6">
          <h2
            id="contact-seller-title"
            className="text-2xl font-semibold text-on-surface mb-2"
          >
            Thank you!
          </h2>
          <p className="text-lg text-secondary-accent">
            Your request has been submitted successfully.
          </p>
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <p className="text-accent text-lg">
              Your Reference ID:{" "}
              <span className="text-xl font-mono font-bold">{requestId}</span>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Please save this ID for any follow-up questions.
            </p>
          </div>
          <button
            onClick={handleModalClose}
            className="mt-4 px-6 py-2 bg-accent text-white rounded hover:bg-accent/90 transition"
          >
            Close
          </button>
        </div>
      ) : (
        <>
          <h2
            id="contact-seller-title"
            className="text-2xl font-bold text-accent mb-6"
          >
            Contact us
          </h2>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-1 gap-y-6 gap-x-8 lg:grid-cols-2"
          >
            {/* Full Name (full width) */}
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-secondary-accent"
              >
                Full Name
              </label>
              <input
                id="fullName"
                {...register("fullName")}
                placeholder="John Doe"
                className="
                  mt-1 block w-full rounded-lg
                  bg-white px-3 py-2
                  text-primary
                  focus:outline-none focus:ring-2 focus:ring-primary
                "
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-danger">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-secondary-accent"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                {...register("email")}
                placeholder="you@example.com"
                className="
                   mt-1 block w-full rounded-lg
                  bg-white px-3 py-2
                  text-primary
                  focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-danger">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-secondary-accent"
              >
                Phone
              </label>
              <input
                id="phone"
                {...register("phone")}
                placeholder="+44 1234 567890"
                className="
                   mt-1 block w-full rounded-lg
                  bg-white px-3 py-2
                  text-primary
                  focus:outline-none focus:ring-2 focus:ring-primary
                "
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-danger">
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* Payment Option */}
            <div>
              <label
                htmlFor="paymentOption"
                className="block text-sm font-medium text-secondary-accent"
              >
                Payment Option
              </label>
              <select
                id="paymentOption"
                {...register("paymentOption")}
                className="
                   mt-1 block w-full rounded-lg
                  bg-white px-3 py-2
                  text-primary
                  focus:outline-none focus:ring-2 focus:ring-primary
                "
              >
                <option value="">Select…</option>
                <option value="full">Pay in full</option>
                <option value="installments">Finance</option>
              </select>
              {errors.paymentOption && (
                <p className="mt-1 text-sm text-danger">
                  {errors.paymentOption.message}
                </p>
              )}
            </div>

            {showFinancePrompt && (
              <div className="lg:col-span-2 mt-4 p-4 border rounded bg-gray-50 text-center">
                <p className="text-lg font-medium mb-4">
                  Use our finance calculator to estimate your payments?
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      handleModalClose();
                      ref.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Show finance inputs in modal
                      setShowFinanceInputs(true);
                      setShowFinancePrompt(false);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {paymentOption === "installments" && showFinanceInputs && (
              <>
                {/* PCP: Only show the required fields */}
                {financeData?.financeType === "PCP" && (
                  <>
                    {/* Vehicle Price */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Vehicle Price
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                        £{vehiclePriceInfo.basePrice}
                      </div>
                    </div>

                    {/* Term Length (read-only, only this) */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Term Length
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                          {financeData.termLength === 24 ? '24 months (Lease Purchase)' : `${financeData.termLength} months`}
                        </div>
                    </div>

                    {/* Agreement Type (read-only, no PCP abbreviation) */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Agreement Type
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                        Personal Contract Purchase (PCP)
                      </div>
                    </div>

                    {/* Annual Mileage (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Annual Mileage
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                        {financeData.annualMileage} miles
                      </div>
                    </div>

                    {/* Per Month (PCP) Value */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Per Month (PCP)
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                        £{financeData.monthlyPayment ? Number(financeData.monthlyPayment).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}
                      </div>
                    </div>

                    {/* Final payment (balloon) */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-accent mb-1">
                        Final payment (balloon)
                      </label>
                      <div className="mb-2 text-base font-semibold text-primary">
                        £{financeData.balloon ? Number(financeData.balloon).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}
                      </div>
                    </div>
                  </>
                )}

                {/* Term Length + Agreement Type in one row */}
                {/* Removed Term Length and Agreement Type selection UI as per new requirements */}

                {/* Expected Annual Mileage dropdown removed for PCP */}
              </>
            )}

            {/* HP summary fields (inserted above Message) */}
            {financeData?.financeType === "HP" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4  rounded-lg lg:col-span-2">
                  {/* Cash Deposit (£) */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Cash Deposit (£)</label>
                    <div className="mb-2 text-base font-semibold text-primary">£{financeData.deposit ? Number(financeData.deposit).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}</div>
                  </div>
                  {/* Term Length */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Term Length</label>
                    <div className="mb-2 text-base font-semibold text-primary">{financeData.termLength} months</div>
                  </div>
                  {/* Agreement Type */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Agreement Type</label>
                    <div className="mb-2 text-base font-semibold text-primary">Hire Purchase (HP)</div>
                  </div>
                  {/* Annual Mileage */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Annual Mileage</label>
                    <div className="mb-2 text-base font-semibold text-primary">{financeData.annualMileage ? Number(financeData.annualMileage).toLocaleString(undefined, {minimumFractionDigits: 0}) : 'miles'}</div>
                  </div>
                  {/* Per Month (HP) */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Per Month (HP)</label>
                    <div className="mb-2 text-base font-semibold text-primary">£{financeData.monthlyPayment ? Number(financeData.monthlyPayment).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}</div>
                  </div>
                  {/* Credit Score */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-accent mb-1">Credit Score</label>
                    <div className="mb-2 text-base font-semibold text-primary">{financeData.creditScore ? financeData.creditScore : ''}</div>
                  </div>
                </div>
              </>
            )}
            {/* Message (span two cols) */}
            <div className="lg:col-span-2">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-secondary-accent"
              >
                Message
              </label>
              <textarea
                id="message"
                {...register("message")}
                rows={4}
                className="
                  mt-1 block w-full rounded-lg
                  bg-white px-3 py-2
                  text-primary
                  focus:outline-none focus:ring-2 focus:ring-primary
                "
              />
              {errors.message && (
                <p className="mt-1 text-sm text-danger">
                  {errors.message.message}
                </p>
              )}
            </div>

            {/* ── Vehicle & Price Summary ── */}
            <div className="lg:col-span-2 w-full mb-6 p-6 border rounded-lg bg-gray-50">
              <h3 className="text-xl font-semibold text-primary mb-4">
                Vehicle Summary
              </h3>

              {/* Vehicle Info */}
              <div className="mb-4">
                <p>
                  <span className="font-medium">Make:</span> {make}
                </p>
                <p>
                  <span className="font-medium">Model:</span> {model}
                </p>
                <p>
                  <span className="font-medium">Mileage:</span> {mileage} miles
                </p>
              </div>

              {/* Vehicle Price and Balloon for PCP only */}
                {/* HP summary fields removed from Vehicle Summary section as per user request */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="
                  px-5 py-2 text-sm font-medium
                  bg-accent text-white rounded-lg
                  hover:bg-accent
                  focus:outline-none focus:ring-2 focus:ring-primary
                  disabled:opacity-50
                "
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
}
